import React, { useState, useRef, useEffect } from "react";
import { FaDownload } from "react-icons/fa";
import { YelpAPI, YelpQuery, YelpResult } from "../lib/yelp";
import { categories } from "../data"; // Import categories
import { useToast } from "../context/ToastContext";

const SearchFilter: React.FC<{
  yelp: YelpAPI;
  setCurrentItems: (items: YelpResult) => void;
  setIsLoading: (loading: boolean) => void;
}> = ({ yelp, setCurrentItems, setIsLoading }) => {
  const [term, setTerm] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showPopover, setShowPopover] = useState(false);
  const [selectedPrices, setSelectedPrices] = useState<string[]>([]); // Change to array for multiple selections
  const [address, setAddress] = useState(""); // New state for address
  const [maxRadius, setMaxRadius] = useState(2); // New state for maximum radius
  const [minRating, setMinRating] = useState(0); // New state for minimum rating
  const [minRatingCount, setMinRatingCount] = useState(0); // New state for minimum rating count
  const [lastSearchQuery, setLastSearchQuery] = useState<YelpQuery | null>(
    null
  );
  const popoverRef = useRef<HTMLDivElement>(null);
  const [hasData, setHasData] = useState(false);
  const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
  const categoriesPerPage = 10; // Number of categories to load at a time
  const { showToast } = useToast();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterButton = document.querySelector(".filter-button-class"); // Add a class to the filter button
      const filterText = document.querySelector(".filter-text-class");
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        filterButton !== event.target &&
        filterText !== event.target
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopover]);

  useEffect(() => {
    // Load initial categories
    setVisibleCategories(categories.slice(0, categoriesPerPage));
  }, []);

  const handleCategoryScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop === clientHeight) {
      // Load more categories when scrolled to the bottom
      setVisibleCategories((prev) => [
        ...prev,
        ...categories.slice(prev.length, prev.length + categoriesPerPage),
      ]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const query: YelpQuery = {
      address,
      categories: selectedCategories,
      price: selectedPrices,
      maxRadius,
      rating: minRating,
      minRatingCount,
      term,
      limit: 250,
    };

    if (
      (lastSearchQuery &&
        JSON.stringify(lastSearchQuery) === JSON.stringify(query)) ||
      query.address === ""
    ) {
      showToast(
        query.address === ""
          ? "Please enter an address or location"
          : "No changes to search query",
        "error"
      );
      setIsLoading(false);
      return;
    }

    const results = await yelp.search(query, (message: string) =>
      showToast(message, "error")
    );
    setCurrentItems(results);
    setLastSearchQuery(query);
    setHasData(true);
    setIsLoading(false);
  };

  const togglePopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPopover((prev) => !prev);
  };

  const handleExport = () => {
    const csv = yelp.exportToCSV();
    // Download the CSV file
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    // Set the filename to the current date and time
    a.download = `results_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((cat) => cat !== category)
        : [...prev, category]
    );
  };

  const handlePriceChange = (price: string) => {
    setSelectedPrices((prevPrices) =>
      prevPrices.includes(price)
        ? prevPrices.filter((p) => p !== price)
        : [...prevPrices, price]
    );
  };

  const handleRatingChange = (rating: number) => {
    setMinRating(rating);
  };

  const handleClearFilters = () => {
    setTerm("");
    setCategorySearch("");
    setSelectedCategories([]);
    setSelectedPrices([]);
    setMaxRadius(2);
    setMinRating(0);
    setMinRatingCount(0);
    setHasData(false);
  };

  const filteredCategories = categories.filter((cat) =>
    cat.title.toLowerCase().includes(categorySearch.toLowerCase())
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="relative flex flex-col items-center justify-center w-full bg-white rounded-lg"
    >
      <div className="flex items-center justify-center w-full">
        <input
          type="text"
          placeholder="Search for an address or location"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="flex-grow p-2 border border-gray-300 rounded-l-lg focus:outline-none"
        />
        <button
          type="button"
          onClick={togglePopover}
          className={`filter-button-class flex items-center justify-center p-2 border border-gray-300 rounded-r-lg ${
            showPopover
              ? "bg-gray-300 text-gray-900"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          <span className="filter-text-class">Filters</span>
        </button>
        <button
          type="submit"
          className="ml-2 p-2 text-white bg-blue-500 rounded-lg"
        >
          Search
        </button>
        <button
          type="button"
          onClick={handleExport}
          className={`ml-2 p-3 text-white rounded-lg ${
            hasData ? "bg-blue-500" : "bg-gray-300"
          }`}
          disabled={!hasData}
        >
          <FaDownload size={16} />
        </button>
      </div>

      {showPopover && (
        <div
          ref={popoverRef}
          className="absolute z-10 p-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
          style={{
            top: "100%",
            right: "0%",
            transform: "translateX(0%)",
            width: "300px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-start w-full">
            <div className="mb-4 w-full">
              <label className="block mb-1">Search Term</label>
              <input
                type="text"
                placeholder="Enter search term"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </div>

            <div className="mb-4 w-full">
              <label className="block mb-1">Category</label>
              <input
                type="text"
                placeholder="Search categories"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="w-full p-2 mb-2 border border-gray-300 rounded-lg"
              />
              <div
                className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg"
                onScroll={handleCategoryScroll}
              >
                {filteredCategories
                  .slice(0, visibleCategories.length)
                  .map((cat) => (
                    <div
                      key={cat.alias}
                      className="flex items-center my-2 ml-2 cursor-pointer"
                      onClick={() => handleCategoryChange(cat.alias)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.alias)}
                        className="mr-2"
                      />
                      <span>{cat.title}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="mb-2 w-full">
              <label className="block mb-1">Maximum Radius (miles)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="24.854848"
                  value={maxRadius}
                  onChange={(e) => setMaxRadius(Number(e.target.value))}
                  className="flex-grow p-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mb-2 w-full">
              <label className="block mb-1">Minimum Number of Reviews</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  value={minRatingCount}
                  onChange={(e) => setMinRatingCount(Number(e.target.value))}
                  className="flex-grow p-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mb-2">
              <label className="block mb-1">Minimum Rating</label>
              <div className="flex items-center">
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="0.1"
                  value={minRating}
                  onChange={(e) => handleRatingChange(Number(e.target.value))}
                  className="flex-grow"
                />
                <span className="ml-2">★ {minRating.toFixed(1)}</span>
              </div>
            </div>

            <div className="mb-2 w-full">
              <label className="block mb-1">Price Range</label>
              <div className="flex">
                {["$", "$$", "$$$", "$$$$"].map((price) => (
                  <div
                    key={price}
                    className={`flex items-center justify-center h-8 m-1 pl-2 pr-2 border rounded cursor-pointer ${
                      selectedPrices.includes(price)
                        ? "bg-gray-300 text-gray-900"
                        : "bg-white text-gray-300"
                    }`}
                    onClick={() => handlePriceChange(price)}
                  >
                    <span>{price}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-gray-500 hover:text-gray-700 text-sm mt-4 self-end"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default SearchFilter;
